/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import camelCase from 'lodash/camelCase';
import pick from 'lodash/pick';
import {ScrollEvent} from '../events/scroll.event';
import {scrollOptions} from '../options/scroll.options';

/**
 * Scroll directive
 *
 * @stability Experimental
 */
export const scrollDirective = {
  DIRECTION_UP: 'up',
  DIRECTION_DOWN: 'down',
  DIRECTION_LEFT: 'left',
  DIRECTION_RIGHT: 'right',

  /**
   * Scroll direction options container
   */
  options: scrollOptions,

  /**
   * A map hold all scroll records
   *
   * @type {Map<string, object}
   */
  records: new Map(),

  /**
   * Initialize and listen to scroll event for the given element
   *
   * @param {HTMLElement} element HTML DOM element
   * @param {object}      options scroll options
   */
  init(element, options = {}) {
    let timer = false;
    const callbacks = pick(options, ['scrollChange', 'scrollUp', 'scrollDown', 'scrollLeft', 'scrollRight', 'scrollDirectionChange', 'scrollHorizontalDirectionChange', 'scrollVerticalDirectionChange']);
    const eventTarget = element instanceof HTMLBodyElement ? document : element;
    const listener = (event) => {
      if (!timer) {
        timer = setTimeout(() => {
          this.onScroll(event, this.records.get(element.ambid));

          timer = false;
        }, options.throttle);
      }
    };

    this.records.set(element.ambid, {
      listener,
      callbacks,
      direction: options.direction,
      scrollX: 0,
      scrollY: 0,
      horizontal: null,
      vertical: null,
    });

    eventTarget.addEventListener('scroll', listener, {passive: true});
  },

  /**
   * Destroy initialized scroll listener for the given element
   *
   * @param {HTMLElement} element HTML DOM element
   */
  destroy(element) {
    const ambid = element.ambid;

    if (this.records.has(ambid)) {
      const {listener} = this.records.get(ambid);
      const eventTarget = element instanceof HTMLBodyElement ? document : element;

      eventTarget.removeEventListener('scroll', listener);
      this.records.delete(ambid);
    }
  },

  /**
   * Fires when scrolling on registered element
   *
   * @protected
   *
   * @param {Event}  event  Scroll event
   * @param {object} record Scroll record registered for this scrolling element
   */
  onScroll(event, record) {
    const {callbacks = {}, direction = 'vertical', scrollX, scrollY, horizontal, vertical} = record;
    const scrollingElement = event.target === document ? document.scrollingElement : event.target;
    const events = new Set();
    const context = {originalEvent: event};

    events.add('scroll.change');

    if (direction === 'both' || direction === 'horizontal') {
      record.horizontal = context.horizontal = scrollingElement.scrollLeft > scrollX ? this.DIRECTION_RIGHT : (scrollingElement.scrollLeft < scrollX ? this.DIRECTION_LEFT : horizontal);
      record.scrollX = context.scrollX = scrollingElement.scrollLeft;

      if (scrollX !== record.scrollX) {
        if (record.horizontal === this.DIRECTION_LEFT) {
          events.add('scroll.left');
        }

        if (record.horizontal === this.DIRECTION_RIGHT) {
          events.add('scroll.right');
        }

        if (horizontal !== record.horizontal) {
          events.add('scroll.direction-change');
          events.add('scroll.horizontal-direction-change');
        }
      }
    }

    if (direction === 'both' || direction === 'vertical') {
      record.vertical = context.vertical = scrollingElement.scrollTop > scrollY ? this.DIRECTION_DOWN : (scrollingElement.scrollTop < scrollY ? this.DIRECTION_UP : vertical);
      record.scrollY = context.scrollY = scrollingElement.scrollTop;

      if (scrollY !== record.scrollY) {
        if (record.vertical === this.DIRECTION_UP) {
          events.add('scroll.up');
        }

        if (record.vertical === this.DIRECTION_DOWN) {
          events.add('scroll.down');
        }

        if (vertical !== record.vertical) {
          events.add('scroll.direction-change');
          events.add('scroll.vertical-direction-change');
        }
      }
    }

    for (const type of events) {
      const callback = camelCase(type);
      const scrollEvent = new ScrollEvent(type, {bubbles: true, cancelable: true}, context);

      if (typeof callbacks[callback] === 'function') {
        callbacks[callback](scrollEvent);
      }

      scrollingElement.dispatchEvent(scrollEvent);
    }
  },
};
